import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';

import { EmspBoEmspUserOcpiTokenCreateController } from '../emsp-bo-emsp-user-ocpi-token-create/emsp-bo-emsp-user-ocpi-token-create.controller';
import { EmspBoEmspUserOcpiTokenDetailsController } from '../emsp-bo-emsp-user-ocpi-token-details/emsp-bo-emsp-user-ocpi-token-details.controller';
import {
  EmspBoEmspUserOcpiTokenContextProvider,
  useEmspBoEmspUserContext,
} from '../emsp-bo-emsp-user.context';

export const EmspBoEmspUserOcpiTokenListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { ocpiTokens } = useEmspBoEmspUserContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canCreate = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.CREATE_EMSP_USER_OCPI_TOKEN,
      }),
    [userPermissions],
  );

  // ACTIONS: CREATE RESOURCE //////////////////////////////////////////////////

  const [actionCreateIsDialogOpen, setActionCreateIsDialogOpen] =
    useState(false);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <>
      <Card direction="column" gap={GapEnum.L}>
        <Stack
          alignItems="center"
          gap={GapEnum.XS}
          justifyContent="space-between">
          <AccentTitle
            icon="credit-card-02"
            text={t('delmonicos-shared-sdk.outputs-emsp-user.ocpi-tokens')}
          />
          {canCreate && (
            <ButtonIcon
              icon="plus"
              onClick={() => setActionCreateIsDialogOpen(true)}
            />
          )}
        </Stack>
        {ocpiTokens.length === 0 ? (
          <Text color={TextColor.Secondary}>
            {t('$.emsp-bo-emsp-user-ocpi-token-list.empty')}
          </Text>
        ) : (
          <Stack direction="column" gap={GapEnum.L}>
            {ocpiTokens.map((token, index) => (
              <EmspBoEmspUserOcpiTokenContextProvider
                key={token.id}
                data={token}>
                <EmspBoEmspUserOcpiTokenDetailsController />
                {index < ocpiTokens.length - 1 && <Divider />}
              </EmspBoEmspUserOcpiTokenContextProvider>
            ))}
          </Stack>
        )}
      </Card>
      <Dialog
        open={actionCreateIsDialogOpen}
        onClose={() => setActionCreateIsDialogOpen(false)}>
        <Dialog.Header
          title={t('$.emsp-bo-emsp-user-ocpi-token-create.title')}
          icon="credit-card-01"
          onClose={() => setActionCreateIsDialogOpen(false)}
        />
        <Dialog.Content>
          <EmspBoEmspUserOcpiTokenCreateController
            onCancel={() => setActionCreateIsDialogOpen(false)}
            onSuccess={() => setActionCreateIsDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
    </>
  );
};
